/**
 * Custom hook for fetching and managing onboarding tasks.
 * Uses the OnboardingTasks API (GET /api/candidates/tasks, PUT /api/candidates/tasks/{id}).
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
  TasksApiClient,
  TasksApiException,
  OnboardingTaskListDto,
  OnboardingTaskDto,
  UpdateOnboardingTaskRequestDto,
} from '../api/tasksApiClient';
import { createApiConfig, createTestApiConfig } from '../api/apiClient';
import { isTestMode, apiConfig, getAuthProvider } from '../../config';
import { appInsights } from '../../telemetry/appInsights';
import { getImpersonationEmail } from '../../utils/impersonation';

interface UseOnboardingTasksResult {
  tasks: OnboardingTaskDto[];
  loading: boolean;
  error: string | null;
  submitting: boolean;
  isImpersonating: boolean;
  refetch: () => Promise<void>;
  updateTask: (taskId: string, request: UpdateOnboardingTaskRequestDto) => Promise<OnboardingTaskDto | null>;
}

/**
 * Hook to fetch and manage onboarding tasks for the authenticated candidate.
 */
export const useOnboardingTasks = (): UseOnboardingTasksResult => {
  const msalContext = useMsal();

  const instance = isTestMode ? null : msalContext?.instance;
  const accounts = isTestMode ? [] : msalContext?.accounts || [];
  const account = accounts[0] || null;
  const impersonationEmail = getImpersonationEmail();
  const isImpersonating = Boolean(impersonationEmail);

  const [taskList, setTaskList] = useState<OnboardingTaskListDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const createClient = useCallback((): TasksApiClient => {
    const config = (isTestMode || !instance || !account)
      ? createTestApiConfig()
      : createApiConfig(instance, account);
    return new TasksApiClient(config, apiConfig.baseUrl);
  }, [instance, account]);

  const fetchTasks = useCallback(async () => {
    if (!isTestMode && !account) {
      setLoading(false);
      setError('No authenticated account found');
      return;
    }

    setLoading(true);
    setError(null);
    if (impersonationEmail) {
      setTaskList(null);
    }

    try {
      const client = createClient();
      const result = impersonationEmail
        ? await client.getTasksByEmail(impersonationEmail)
        : await client.getTasks();
      setTaskList(result);
    } catch (err) {
      console.error('Error fetching onboarding tasks:', err);

      appInsights.trackException({
        exception: err instanceof Error ? err : new Error(String(err)),
        severityLevel: SeverityLevel.Error,
        properties: {
          component: 'useOnboardingTasks',
          action: 'fetchTasks',
          statusCode: err instanceof TasksApiException ? String(err.status) : 'unknown',
          authProvider: isTestMode ? 'test' : getAuthProvider(),
          isImpersonating,
        },
      });

      if (err instanceof TasksApiException) {
        switch (err.status) {
          case 401:
            setError('Session expired. Please refresh the page.');
            break;
          case 403:
            setError(
              isImpersonating
                ? 'You do not have permission to view this candidate\'s onboarding tasks.'
                : 'You do not have access to view tasks.'
            );
            break;
          default:
            setError('Unable to load onboarding tasks. Please try again later.');
        }
      } else {
        setError('Unable to load onboarding tasks. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [account, createClient, impersonationEmail, isImpersonating]);

  const updateTask = useCallback(async (
    taskId: string,
    request: UpdateOnboardingTaskRequestDto
  ): Promise<OnboardingTaskDto | null> => {
    setSubmitting(true);

    try {
      const client = createClient();
      const updatedTask = impersonationEmail
        ? await client.updateTaskByEmail(impersonationEmail, taskId, request)
        : await client.updateTask(taskId, request);

      // Update local state with the updated task
      setTaskList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map((t) => (t.id === taskId ? updatedTask : t)),
        };
      });

      appInsights.trackEvent({
        name: 'OnboardingTaskCompleted',
        properties: {
          taskId,
          taskType: updatedTask.taskType,
          status: updatedTask.status,
        },
      });

      return updatedTask;
    } catch (err) {
      console.error('Error updating onboarding task:', err);

      appInsights.trackException({
        exception: err instanceof Error ? err : new Error(String(err)),
        severityLevel: SeverityLevel.Error,
        properties: {
          component: 'useOnboardingTasks',
          action: 'updateTask',
          taskId,
          statusCode: err instanceof TasksApiException ? String(err.status) : 'unknown',
          authProvider: isTestMode ? 'test' : getAuthProvider(),
        },
      });

      if (err instanceof TasksApiException) {
        switch (err.status) {
          case 409:
            setError('This task cannot be updated in its current state.');
            break;
          case 422:
            setError('Please check your responses and try again.');
            break;
          default:
            setError('Unable to submit your response. Please try again.');
        }
      } else {
        setError('Unable to submit your response. Please try again.');
      }

      return null;
    } finally {
      setSubmitting(false);
    }
  }, [createClient, impersonationEmail]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return {
    tasks: taskList?.tasks || [],
    loading,
    error,
    submitting,
    isImpersonating,
    refetch: fetchTasks,
    updateTask,
  };
};
